import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table, Tabs, Tab } from 'react-bootstrap';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../services/apiClient';
import UploadDropzone from '../components/UploadDropzone';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';

const EMPTY_FORM = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  image_url: '',
  status: 'draft',
  published_at: '',
};

const DEFAULT_WHY = {
  eyebrow: 'Why Homevilas',
  title: 'Travel with confidence',
  subtitle: 'Luxury stays, transparent pricing, and an on-ground team that never leaves you guessing.',
  background_image_url: '',
  items: [],
};

const EMPTY_WHY_VIDEO = {
  id: null,
  title: '',
  description: '',
  video_url: '',
  poster_image_url: '',
  category: 'client_testimonial',
  is_active: true,
  is_featured: false,
  sort_order: 1,
};

const WHY_VIDEO_CATEGORY_OPTIONS = [
  { value: 'client_testimonial', label: 'Client testimonial' },
  { value: 'influencer_promo', label: 'Influencer promo' },
  { value: 'brand_promo', label: 'Brand promo' },
];

const WHY_VIDEO_CATEGORY_LABELS = WHY_VIDEO_CATEGORY_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const toDateInput = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch (_) {
    return '';
  }
};

const resolveWhyVideoCategoryLabel = (value) =>
  WHY_VIDEO_CATEGORY_LABELS[String(value || '').trim()] || 'Client testimonial';

const ContentBlogsPage = ({ token }) => {
  const [activeTab, setActiveTab] = useState('blogs');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);

  const [faqItems, setFaqItems] = useState([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [faqError, setFaqError] = useState('');
  const [faqEditing, setFaqEditing] = useState(null);
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', status: 'draft', sort_order: 0 });

  const [whyForm, setWhyForm] = useState(DEFAULT_WHY);
  const [whyItemForm, setWhyItemForm] = useState({
    id: null,
    title: '',
    desc: '',
    background_image_url: '',
    is_active: true,
    sort_order: 1,
  });
  const [whyLoading, setWhyLoading] = useState(false);
  const [whySaving, setWhySaving] = useState(false);
  const [whyError, setWhyError] = useState('');
  const [whyVideos, setWhyVideos] = useState([]);
  const [whyVideoForm, setWhyVideoForm] = useState(EMPTY_WHY_VIDEO);
  const [whyVideosLoading, setWhyVideosLoading] = useState(false);
  const [whyVideoSaving, setWhyVideoSaving] = useState(false);
  const [whyVideoError, setWhyVideoError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/admin/content/blogs?limit=200', token);
      const list = res?.data || res?.items || res || [];
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      setItems([]);
      setError('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const loadFaqs = async () => {
    setFaqLoading(true);
    setFaqError('');
    try {
      const res = await apiGet('/api/admin/content/faqs', token);
      setFaqItems(res.data || res);
    } catch (err) {
      setFaqError('Failed to load FAQs');
    } finally {
      setFaqLoading(false);
    }
  };

  const loadWhy = async () => {
    setWhyLoading(true);
    setWhyError('');
    try {
      const res = await apiGet('/api/admin/content/why-homevilas', token);
      const value = res?.value || res?.data?.value || res;
      if (value && typeof value === 'object') {
        setWhyForm({
          eyebrow: value.eyebrow || DEFAULT_WHY.eyebrow,
          title: value.title || DEFAULT_WHY.title,
          subtitle: value.subtitle || DEFAULT_WHY.subtitle,
          background_image_url: value.background_image_url || '',
          items: Array.isArray(value.items) ? value.items : [],
        });
      } else {
        setWhyForm(DEFAULT_WHY);
      }
    } catch (err) {
      setWhyForm(DEFAULT_WHY);
      setWhyError('Failed to load Why Homevilas content.');
    } finally {
      setWhyLoading(false);
    }
  };

  const loadWhyVideos = async () => {
    setWhyVideosLoading(true);
    setWhyVideoError('');
    try {
      const res = await apiGet('/api/admin/content/why-homevilas/videos', token);
      const list = res?.data || res || [];
      setWhyVideos(Array.isArray(list) ? list : []);
    } catch (err) {
      setWhyVideos([]);
      setWhyVideoError(err.message || 'Failed to load Why Homevilas videos.');
    } finally {
      setWhyVideosLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadFaqs();
    loadWhy();
    loadWhyVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setSlugTouched(false);
  };

  const onEdit = (row) => {
    setEditing(row);
    setSlugTouched(true);
    setForm({
      title: row.title || '',
      slug: row.slug || '',
      excerpt: row.excerpt || '',
      body: row.body || '',
      image_url: row.image_url || '',
      status: row.status || 'draft',
      published_at: toDateInput(row.published_at || row.created_at),
    });
  };

  const save = async () => {
    setLoading(true);
    setError('');
    try {
      const slugValue = String(form.slug || '').trim() || slugify(form.title);
      const trimmedBody = String(form.body || '').trim();
      const excerptValue = String(form.excerpt || '').trim() || (trimmedBody ? `${trimmedBody.slice(0, 160)}...` : '');
      const payload = {
        title: String(form.title || '').trim(),
        slug: slugValue,
        excerpt: excerptValue,
        body: trimmedBody,
        image_url: String(form.image_url || '').trim(),
        status: form.status || 'draft',
        published_at: form.published_at
          ? new Date(form.published_at).toISOString()
          : form.status === 'published'
            ? new Date().toISOString()
            : null,
      };

      if (!payload.title) throw new Error('Title is required.');
      if (!payload.slug) throw new Error('Slug is required.');

      const url = editing ? `/api/admin/content/blogs/${editing.id}` : '/api/admin/content/blogs';
      await apiPost(url, payload, token);
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete blog?')) return;
    try {
      await apiDelete(`/api/admin/content/blog_posts/${id}`, token);
      await load();
      if (editing?.id === id) resetForm();
    } catch (err) {
      setError('Delete failed');
    }
  };

  const saveFaq = async () => {
    setFaqLoading(true);
    setFaqError('');
    try {
      const url = faqEditing ? `/api/admin/content/faqs/${faqEditing.id}` : '/api/admin/content/faqs';
      await apiPost(url, faqForm, token);
      setFaqEditing(null);
      setFaqForm({ question: '', answer: '', status: 'draft', sort_order: 0 });
      await loadFaqs();
    } catch (err) {
      setFaqError('Save failed');
    } finally {
      setFaqLoading(false);
    }
  };

  const removeFaq = async (id) => {
    if (!window.confirm('Delete FAQ?')) return;
    try {
      await apiDelete(`/api/admin/content/faqs/${id}`, token);
      await loadFaqs();
    } catch (err) {
      setFaqError('Delete failed');
    }
  };

  const sortedWhyItems = useMemo(() => {
    const list = Array.isArray(whyForm.items) ? whyForm.items : [];
    return [...list].sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0));
  }, [whyForm.items]);

  const sortedWhyVideos = useMemo(() => {
    const list = Array.isArray(whyVideos) ? whyVideos : [];
    return [...list].sort(
      (a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0) || Number(a.id ?? 0) - Number(b.id ?? 0)
    );
  }, [whyVideos]);

  const resetWhyItemForm = () => {
    setWhyItemForm({
      id: null,
      title: '',
      desc: '',
      background_image_url: '',
      is_active: true,
      sort_order: sortedWhyItems.length + 1,
    });
  };

  const onEditWhyItem = (item) => {
    setWhyItemForm({
      id: item.id || item.title,
      title: item.title || '',
      desc: item.desc || item.description || '',
      background_image_url: item.background_image_url || item.backgroundImageUrl || item.backgroundImage || '',
      is_active: item.is_active !== false,
      sort_order: Number(item.sort_order ?? 0),
    });
  };

  const saveWhyItem = () => {
    setWhyError('');
    if (!String(whyItemForm.title || '').trim()) {
      setWhyError('Title is required for a Why Homevilas item.');
      return;
    }
    setWhyForm((prev) => {
      const items = Array.isArray(prev.items) ? [...prev.items] : [];
      const id = whyItemForm.id || `why-${Date.now()}`;
      const next = {
        ...whyItemForm,
        id,
        title: String(whyItemForm.title).trim(),
        background_image_url: String(whyItemForm.background_image_url || '').trim(),
      };
      const idx = items.findIndex((row) => row.id === id);
      if (idx >= 0) items[idx] = next;
      else items.push(next);
      return { ...prev, items };
    });
    resetWhyItemForm();
  };

  const deleteWhyItem = (id) => {
    setWhyForm((prev) => ({
      ...prev,
      items: (prev.items || []).filter((item) => item.id !== id),
    }));
    if (whyItemForm.id === id) resetWhyItemForm();
  };

  const resetWhyVideoForm = () => {
    setWhyVideoError('');
    setWhyVideoForm({
      ...EMPTY_WHY_VIDEO,
      sort_order: sortedWhyVideos.length + 1,
    });
  };

  const onEditWhyVideo = (video) => {
    setWhyVideoError('');
    setWhyVideoForm({
      id: video.id,
      title: video.title || '',
      description: video.description || '',
      video_url: video.video_url || '',
      poster_image_url: video.poster_image_url || '',
      category: video.category || 'client_testimonial',
      is_active: video.is_active !== false,
      is_featured: video.is_featured === true,
      sort_order: Number(video.sort_order ?? 1),
    });
  };

  const saveWhyVideo = async () => {
    setWhyVideoSaving(true);
    setWhyVideoError('');
    try {
      const payload = {
        title: String(whyVideoForm.title || '').trim(),
        description: String(whyVideoForm.description || '').trim(),
        video_url: String(whyVideoForm.video_url || '').trim(),
        poster_image_url: String(whyVideoForm.poster_image_url || '').trim(),
        category: String(whyVideoForm.category || 'client_testimonial').trim(),
        is_active: whyVideoForm.is_active !== false,
        is_featured: whyVideoForm.is_featured === true,
        sort_order: Math.max(1, Number(whyVideoForm.sort_order || 1)),
      };

      if (!payload.title) throw new Error('Video title is required.');
      if (!payload.video_url) throw new Error('Video file or MP4 URL is required.');

      if (whyVideoForm.id) {
        await apiPut(`/api/admin/content/why-homevilas/videos/${whyVideoForm.id}`, payload, token);
      } else {
        await apiPost('/api/admin/content/why-homevilas/videos', payload, token);
      }

      await loadWhyVideos();
      resetWhyVideoForm();
    } catch (err) {
      setWhyVideoError(err.message || 'Failed to save Why Homevilas video.');
    } finally {
      setWhyVideoSaving(false);
    }
  };

  const deleteWhyVideo = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await apiDelete(`/api/admin/content/why-homevilas/videos/${id}`, token);
      await loadWhyVideos();
      if (whyVideoForm.id === id) resetWhyVideoForm();
    } catch (err) {
      setWhyVideoError(err.message || 'Failed to delete Why Homevilas video.');
    }
  };

  const updateWhyVideoSort = (id, value) => {
    const nextValue = Math.max(1, Number(value || 1));
    setWhyVideos((prev) =>
      (Array.isArray(prev) ? prev : []).map((video) =>
        video.id === id ? { ...video, sort_order: nextValue } : video
      )
    );
  };

  const saveWhyVideoOrder = async () => {
    if (!sortedWhyVideos.length) return;
    setWhyVideosLoading(true);
    setWhyVideoError('');
    try {
      await apiPatch(
        '/api/admin/content/why-homevilas/videos/reorder',
        {
          items: sortedWhyVideos.map((video, index) => ({
            id: video.id,
            sort_order: Math.max(1, Number(video.sort_order ?? index + 1)),
          })),
        },
        token
      );
      await loadWhyVideos();
    } catch (err) {
      setWhyVideoError(err.message || 'Failed to save video order.');
      setWhyVideosLoading(false);
    }
  };

  const saveWhyContent = async () => {
    setWhySaving(true);
    setWhyError('');
    try {
      const payload = {
        eyebrow: String(whyForm.eyebrow || '').trim(),
        title: String(whyForm.title || '').trim(),
        subtitle: String(whyForm.subtitle || '').trim(),
        background_image_url: String(whyForm.background_image_url || '').trim(),
        items: sortedWhyItems.map((item, idx) => ({
          id: item.id || `item-${idx + 1}`,
          title: String(item.title || '').trim(),
          desc: String(item.desc || '').trim(),
          background_image_url: String(item.background_image_url || '').trim(),
          is_active: item.is_active !== false,
          sort_order: Number(item.sort_order ?? idx + 1),
        })),
      };
      await apiPost('/api/admin/content/why-homevilas', payload, token);
      await loadWhy();
    } catch (err) {
      setWhyError(err.message || 'Failed to save Why Homevilas content.');
    } finally {
      setWhySaving(false);
    }
  };

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        const aDate = a.published_at || a.created_at || '';
        const bDate = b.published_at || b.created_at || '';
        return String(bDate).localeCompare(String(aDate));
      }),
    [items]
  );

  const previewUrl = resolveAssetUrl(form.image_url);
  const whyVideoPreviewUrl = resolveAssetUrl(whyVideoForm.video_url);
  const whyVideoPosterPreviewUrl = resolveAssetUrl(whyVideoForm.poster_image_url);

  return (
    <div className="p-4 bg-white rounded shadow-sm">
      <div className="mb-3">
        <h4 className="mb-0">Blogs / FAQs</h4>
        <small className="text-muted">Manage blog posts, FAQs, and the Why Homevilas section.</small>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'blogs')} className="mb-3">
        <Tab eventKey="blogs" title="Blogs">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div className="text-muted small">These entries power the landing page blog strip.</div>
            <Button variant="outline-secondary" onClick={load} disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" /> Refresh
                </>
              ) : (
                'Refresh'
              )}
            </Button>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="g-3">
            <Col lg={5}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-semibold">{editing ? 'Edit blog' : 'Add blog'}</div>
                      <div className="text-muted small">These entries power the landing page blog strip.</div>
                    </div>
                    {editing ? (
                      <Button size="sm" variant="outline-secondary" onClick={resetForm}>
                        Cancel
                      </Button>
                    ) : null}
                  </div>

                  <Form className="mt-3" onSubmit={(e) => e.preventDefault()}>
                    <Row className="g-2">
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Title</Form.Label>
                        <Form.Control
                          value={form.title}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForm((p) => ({ ...p, title: value, ...(slugTouched ? {} : { slug: slugify(value) }) }));
                          }}
                          placeholder="Blog title"
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Slug</Form.Label>
                        <Form.Control
                          value={form.slug}
                          onChange={(e) => {
                            setSlugTouched(true);
                            setForm((p) => ({ ...p, slug: e.target.value }));
                          }}
                          placeholder="blog-title"
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Excerpt</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={form.excerpt}
                          onChange={(e) => setForm((p) => ({ ...p, excerpt: e.target.value }))}
                          placeholder="Short summary for the landing page"
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Body</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          value={form.body}
                          onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
                          placeholder="Full blog content (markdown supported)"
                        />
                      </Col>

                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Cover image</Form.Label>
                        {previewUrl ? (
                          <div className="mb-2 rounded border" style={{ height: 160, overflow: 'hidden', background: '#f1f5f9' }}>
                            <img
                              src={previewUrl}
                              alt="Preview"
                              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                            />
                          </div>
                        ) : null}
                        <UploadDropzone
                          token={token}
                          uploadPath="/api/admin/content/banners/upload"
                          label="Upload cover image"
                          onUploaded={(path) => setForm((p) => ({ ...p, image_url: path }))}
                          onUploadedMany={(paths) => setForm((p) => ({ ...p, image_url: paths?.[0] || p.image_url }))}
                        />
                      </Col>

                      <Col md={6}>
                        <Form.Label className="small text-muted mb-1">Status</Form.Label>
                        <Form.Select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                        </Form.Select>
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small text-muted mb-1">Publish date</Form.Label>
                        <Form.Control
                          type="date"
                          value={form.published_at}
                          onChange={(e) => setForm((p) => ({ ...p, published_at: e.target.value }))}
                        />
                      </Col>

                      <Col md={12} className="d-flex gap-2 pt-2">
                        <Button onClick={save} disabled={loading}>
                          {editing ? 'Update' : 'Create'}
                        </Button>
                        <Button variant="outline-secondary" onClick={resetForm} disabled={loading}>
                          Reset
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={7}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="fw-semibold">Blog posts</div>
                    <div className="text-muted small">{sortedItems.length} item(s)</div>
                  </div>

                  {loading && (
                    <div className="py-3 text-center text-muted">
                      <Spinner size="sm" className="me-2" /> Loading...
                    </div>
                  )}

                  <div style={{ overflowX: 'auto' }}>
                    <Table hover responsive bordered size="sm" className="mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 92 }}>Image</th>
                          <th>Title</th>
                          <th style={{ width: 140 }}>Status</th>
                          <th style={{ width: 140 }}>Publish date</th>
                          <th style={{ width: 190 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedItems.map((blog) => (
                          <tr key={blog.id}>
                            <td>
                              {blog.image_url ? (
                                <img
                                  src={resolveAssetUrl(blog.image_url)}
                                  alt={blog.title || 'Blog'}
                                  style={{ width: 70, height: 56, objectFit: 'cover', borderRadius: 10 }}
                                  loading="lazy"
                                />
                              ) : (
                                <span className="text-muted small">No image</span>
                              )}
                            </td>
                            <td className="text-truncate" style={{ maxWidth: 260 }}>
                              <div className="fw-semibold">{blog.title}</div>
                              <div className="text-muted small">{blog.slug}</div>
                            </td>
                            <td>
                              <Badge bg={blog.status === 'published' ? 'success' : 'secondary'}>{blog.status}</Badge>
                            </td>
                            <td className="text-muted small">
                              {toDateInput(blog.published_at || blog.created_at) || '-'}
                            </td>
                            <td className="text-nowrap">
                              <Button size="sm" variant="outline-primary" onClick={() => onEdit(blog)}>
                                Edit
                              </Button>{' '}
                              <Button size="sm" variant="outline-danger" onClick={() => remove(blog.id)}>
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {!loading && sortedItems.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center text-muted">
                              No blogs yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="faqs" title="FAQs">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div className="text-muted small">Manage FAQ entries for the landing page.</div>
            <Button variant="outline-secondary" onClick={loadFaqs} disabled={faqLoading}>
              {faqLoading ? (
                <>
                  <Spinner size="sm" className="me-2" /> Refresh
                </>
              ) : (
                'Refresh'
              )}
            </Button>
          </div>
          {faqError && <Alert variant="danger">{faqError}</Alert>}
          <Form className="mb-3" onSubmit={(e) => e.preventDefault()}>
            <Row className="g-2">
              <Col md={4}>
                <Form.Control
                  placeholder="Question"
                  value={faqForm.question}
                  onChange={(e) => setFaqForm((p) => ({ ...p, question: e.target.value }))}
                />
              </Col>
              <Col md={4}>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Answer"
                  value={faqForm.answer}
                  onChange={(e) => setFaqForm((p) => ({ ...p, answer: e.target.value }))}
                />
              </Col>
              <Col md={2}>
                <Form.Select
                  value={faqForm.status}
                  onChange={(e) => setFaqForm((p) => ({ ...p, status: e.target.value }))}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </Form.Select>
              </Col>
              <Col md={1}>
                <Form.Control
                  type="number"
                  placeholder="Order"
                  value={faqForm.sort_order}
                  onChange={(e) => setFaqForm((p) => ({ ...p, sort_order: Number(e.target.value) }))}
                />
              </Col>
              <Col md={1}>
                <Button onClick={saveFaq} disabled={faqLoading}>
                  {faqEditing ? 'Update' : 'Add'}
                </Button>
              </Col>
            </Row>
          </Form>
          {faqLoading && (
            <div className="py-3 text-center">
              <Spinner animation="border" />
            </div>
          )}
          <Table hover responsive bordered size="sm">
            <thead className="table-light">
              <tr>
                <th>Question</th>
                <th>Status</th>
                <th>Order</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {faqItems.map((faq) => (
                <tr key={faq.id}>
                  <td>{faq.question}</td>
                  <td>
                    <Badge bg={faq.status === 'published' ? 'success' : 'secondary'}>{faq.status}</Badge>
                  </td>
                  <td>{faq.sort_order ?? 0}</td>
                  <td className="text-nowrap">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => {
                        setFaqEditing(faq);
                        setFaqForm({ ...faq });
                      }}
                    >
                      Edit
                    </Button>{' '}
                    <Button size="sm" variant="outline-danger" onClick={() => removeFaq(faq.id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
              {faqItems.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center text-muted">
                    No FAQs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Tab>

        <Tab eventKey="why" title="Why Homevilas">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div className="text-muted small">
              Manage the Why Homevilas section header, fallback cards, and premium video gallery.
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  loadWhy();
                  loadWhyVideos();
                }}
                disabled={whyLoading || whyVideosLoading}
              >
                {whyLoading || whyVideosLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Refresh
                  </>
                ) : (
                  'Refresh'
                )}
              </Button>
              <Button variant="primary" onClick={saveWhyContent} disabled={whySaving}>
                {whySaving ? 'Saving...' : 'Save section'}
              </Button>
            </div>
          </div>
          {whyError && <Alert variant="danger">{whyError}</Alert>}
          {whyVideoError && <Alert variant="danger">{whyVideoError}</Alert>}

          <Row className="g-3">
            <Col lg={5}>
              <Card>
                <Card.Body>
                  <div className="fw-semibold mb-2">Section header</div>
                  <div className="text-muted small mb-3">
                    This content is used for the carousel heading and for the static fallback when no videos are active.
                  </div>
                  <Form>
                    <Row className="g-2">
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Eyebrow</Form.Label>
                        <Form.Control
                          value={whyForm.eyebrow}
                          onChange={(e) => setWhyForm((p) => ({ ...p, eyebrow: e.target.value }))}
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Background image</Form.Label>
                        {whyForm.background_image_url ? (
                          <div className="mb-2 rounded border" style={{ height: 140, overflow: 'hidden', background: '#f1f5f9' }}>
                            <img
                              src={resolveAssetUrl(whyForm.background_image_url)}
                              alt="Why Homevilas background"
                              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                            />
                          </div>
                        ) : null}
                        <UploadDropzone
                          token={token}
                          uploadPath="/api/admin/content/banners/upload"
                          label="Upload background image"
                          onUploaded={(path) => setWhyForm((p) => ({ ...p, background_image_url: path }))}
                          onUploadedMany={(paths) =>
                            setWhyForm((p) => ({ ...p, background_image_url: paths?.[0] || p.background_image_url }))
                          }
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Title</Form.Label>
                        <Form.Control
                          value={whyForm.title}
                          onChange={(e) => setWhyForm((p) => ({ ...p, title: e.target.value }))}
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Subtitle</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={whyForm.subtitle}
                          onChange={(e) => setWhyForm((p) => ({ ...p, subtitle: e.target.value }))}
                        />
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>

              <Card className="mt-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="fw-semibold">{whyItemForm.id ? 'Edit fallback card' : 'Add fallback card'}</div>
                    {whyItemForm.id ? (
                      <Button size="sm" variant="outline-secondary" onClick={resetWhyItemForm}>
                        Cancel
                      </Button>
                    ) : null}
                  </div>
                  <div className="text-muted small mt-2">
                    These cards render only when the video gallery has no active videos.
                  </div>
                  <Form className="mt-3" onSubmit={(e) => e.preventDefault()}>
                    <Row className="g-2">
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Title</Form.Label>
                        <Form.Control
                          value={whyItemForm.title}
                          onChange={(e) => setWhyItemForm((p) => ({ ...p, title: e.target.value }))}
                          placeholder="e.g. Verified Properties"
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Description</Form.Label>
                        <Form.Control
                          value={whyItemForm.desc}
                          onChange={(e) => setWhyItemForm((p) => ({ ...p, desc: e.target.value }))}
                          placeholder="Short benefit description"
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Item background image</Form.Label>
                        {whyItemForm.background_image_url ? (
                          <div className="mb-2 rounded border" style={{ height: 120, overflow: 'hidden', background: '#f1f5f9' }}>
                            <img
                              src={resolveAssetUrl(whyItemForm.background_image_url)}
                              alt="Why item background"
                              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                            />
                          </div>
                        ) : null}
                        <UploadDropzone
                          token={token}
                          uploadPath="/api/admin/content/banners/upload"
                          label="Upload item background"
                          onUploaded={(path) => setWhyItemForm((p) => ({ ...p, background_image_url: path })) }
                          onUploadedMany={(paths) =>
                            setWhyItemForm((p) => ({ ...p, background_image_url: paths?.[0] || p.background_image_url }))
                          }
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small text-muted mb-1">Active</Form.Label>
                        <Form.Check
                          type="switch"
                          id="why-fallback-active"
                          checked={whyItemForm.is_active !== false}
                          onChange={(e) => setWhyItemForm((p) => ({ ...p, is_active: e.target.checked }))}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small text-muted mb-1">Sort order</Form.Label>
                        <Form.Control
                          type="number"
                          value={Number(whyItemForm.sort_order ?? 0)}
                          onChange={(e) => setWhyItemForm((p) => ({ ...p, sort_order: Number(e.target.value || 0) }))}
                        />
                      </Col>
                      <Col md={12} className="d-flex gap-2 pt-2">
                        <Button onClick={saveWhyItem}>Save item</Button>
                        <Button variant="outline-secondary" onClick={resetWhyItemForm}>
                          Reset
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>

              <Card className="mt-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="fw-semibold">{whyVideoForm.id ? 'Edit video' : 'Add video'}</div>
                    {whyVideoForm.id ? (
                      <Button size="sm" variant="outline-secondary" onClick={resetWhyVideoForm}>
                        Cancel
                      </Button>
                    ) : null}
                  </div>
                  <div className="text-muted small mt-2">
                    Videos drive the premium landing page carousel. Keep them short, horizontal, and MP4 for best playback.
                  </div>
                  <Form className="mt-3" onSubmit={(e) => e.preventDefault()}>
                    <Row className="g-2">
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Title</Form.Label>
                        <Form.Control
                          value={whyVideoForm.title}
                          onChange={(e) => setWhyVideoForm((p) => ({ ...p, title: e.target.value }))}
                          placeholder="Guest arrival in Santorini"
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Short description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={whyVideoForm.description}
                          onChange={(e) => setWhyVideoForm((p) => ({ ...p, description: e.target.value }))}
                          placeholder="A concise line for the carousel overlay."
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Video file or URL</Form.Label>
                        <Form.Control
                          value={whyVideoForm.video_url}
                          onChange={(e) => setWhyVideoForm((p) => ({ ...p, video_url: e.target.value }))}
                          placeholder="/uploads/why-homevilas/videos/example.mp4"
                        />
                      </Col>
                      <Col md={12}>
                        {whyVideoPreviewUrl ? (
                          <div className="mb-2 rounded border overflow-hidden" style={{ background: '#0f172a' }}>
                            <video
                              src={whyVideoPreviewUrl}
                              poster={whyVideoPosterPreviewUrl || undefined}
                              controls
                              muted
                              preload="metadata"
                              style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }}
                            />
                          </div>
                        ) : null}
                        <UploadDropzone
                          token={token}
                          uploadPath="/api/admin/content/why-homevilas/videos/upload"
                          label="Upload MP4 video"
                          accept="video/mp4,.mp4"
                          fieldName="video"
                          multiple={false}
                          helperText="Drag & drop or click to upload one MP4 video (max 80MB)"
                          onUploaded={(path) => setWhyVideoForm((p) => ({ ...p, video_url: path }))}
                          onUploadedMany={(paths) => setWhyVideoForm((p) => ({ ...p, video_url: paths?.[0] || p.video_url }))}
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Poster image</Form.Label>
                        <Form.Control
                          value={whyVideoForm.poster_image_url}
                          onChange={(e) => setWhyVideoForm((p) => ({ ...p, poster_image_url: e.target.value }))}
                          placeholder="/uploads/why-homevilas/posters/example.jpg"
                        />
                      </Col>
                      <Col md={12}>
                        {whyVideoPosterPreviewUrl ? (
                          <div className="mb-2 rounded border" style={{ height: 140, overflow: 'hidden', background: '#f1f5f9' }}>
                            <img
                              src={whyVideoPosterPreviewUrl}
                              alt="Why Homevilas video poster"
                              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                            />
                          </div>
                        ) : null}
                        <UploadDropzone
                          token={token}
                          uploadPath="/api/admin/content/why-homevilas/videos/poster-upload"
                          label="Upload poster image"
                          accept="image/jpeg,image/png,image/webp"
                          fieldName="image"
                          multiple={false}
                          helperText="Drag & drop or click to upload one JPG, PNG, or WebP poster (max 5MB)"
                          onUploaded={(path) => setWhyVideoForm((p) => ({ ...p, poster_image_url: path }))}
                          onUploadedMany={(paths) =>
                            setWhyVideoForm((p) => ({ ...p, poster_image_url: paths?.[0] || p.poster_image_url }))
                          }
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Category</Form.Label>
                        <Form.Select
                          value={whyVideoForm.category}
                          onChange={(e) => setWhyVideoForm((p) => ({ ...p, category: e.target.value }))}
                        >
                          {WHY_VIDEO_CATEGORY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small text-muted mb-1">Active</Form.Label>
                        <Form.Check
                          type="switch"
                          id="why-video-active"
                          checked={whyVideoForm.is_active !== false}
                          onChange={(e) => setWhyVideoForm((p) => ({ ...p, is_active: e.target.checked }))}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small text-muted mb-1">Featured</Form.Label>
                        <Form.Check
                          type="switch"
                          id="why-video-featured"
                          checked={whyVideoForm.is_featured === true}
                          onChange={(e) => setWhyVideoForm((p) => ({ ...p, is_featured: e.target.checked }))}
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">Sort order</Form.Label>
                        <Form.Control
                          type="number"
                          value={Number(whyVideoForm.sort_order ?? 1)}
                          onChange={(e) =>
                            setWhyVideoForm((p) => ({ ...p, sort_order: Math.max(1, Number(e.target.value || 1)) }))
                          }
                        />
                      </Col>
                      <Col md={12} className="d-flex gap-2 pt-2">
                        <Button onClick={saveWhyVideo} disabled={whyVideoSaving}>
                          {whyVideoSaving ? 'Saving...' : whyVideoForm.id ? 'Update video' : 'Save video'}
                        </Button>
                        <Button variant="outline-secondary" onClick={resetWhyVideoForm} disabled={whyVideoSaving}>
                          Reset
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={7}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <div>
                      <div className="fw-semibold">Video gallery</div>
                      <div className="text-muted small">
                        Active videos power the landing page carousel. When none are active, the fallback cards below render instead.
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-secondary" onClick={loadWhyVideos} disabled={whyVideosLoading}>
                        {whyVideosLoading ? 'Refreshing...' : 'Refresh'}
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={saveWhyVideoOrder}
                        disabled={whyVideosLoading || sortedWhyVideos.length === 0}
                      >
                        Save video order
                      </Button>
                    </div>
                  </div>

                  {whyVideosLoading ? (
                    <div className="py-3 text-center text-muted">
                      <Spinner size="sm" className="me-2" /> Loading videos...
                    </div>
                  ) : null}

                  <div style={{ overflowX: 'auto' }}>
                    <Table hover responsive bordered size="sm" className="mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 110 }}>Poster</th>
                          <th>Video</th>
                          <th style={{ width: 150 }}>Category</th>
                          <th style={{ width: 140 }}>Status</th>
                          <th style={{ width: 96 }}>Sort</th>
                          <th style={{ width: 190 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedWhyVideos.map((video) => {
                          const posterUrl = resolveAssetUrl(video.poster_image_url);
                          const videoUrl = resolveAssetUrl(video.video_url);
                          return (
                            <tr key={video.id}>
                              <td>
                                {videoUrl ? (
                                  <video
                                    src={videoUrl}
                                    poster={posterUrl || undefined}
                                    controls
                                    muted
                                    playsInline
                                    preload="metadata"
                                    style={{
                                      width: 120,
                                      height: 72,
                                      objectFit: 'cover',
                                      borderRadius: 8,
                                      background: '#0f172a',
                                      display: 'block',
                                    }}
                                  />
                                ) : posterUrl ? (
                                  <img
                                    src={posterUrl}
                                    alt={video.title || 'Why Homevilas video poster'}
                                    style={{ width: 120, height: 72, objectFit: 'cover', borderRadius: 8 }}
                                    loading="lazy"
                                  />
                                ) : (
                                  <span className="text-muted small">No media</span>
                                )}
                              </td>
                              <td style={{ maxWidth: 280 }}>
                                <div className="fw-semibold">{video.title}</div>
                                <div className="text-muted small">{video.description || 'No description'}</div>
                                {videoUrl ? (
                                  <a className="small" href={videoUrl} target="_blank" rel="noreferrer">
                                    Open video
                                  </a>
                                ) : null}
                              </td>
                              <td>
                                <Badge bg="dark">{resolveWhyVideoCategoryLabel(video.category)}</Badge>
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-1">
                                  <Badge bg={video.is_active !== false ? 'success' : 'secondary'}>
                                    {video.is_active !== false ? 'Active' : 'Hidden'}
                                  </Badge>
                                  {video.is_featured === true ? <Badge bg="warning" text="dark">Featured</Badge> : null}
                                </div>
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  value={Number(video.sort_order ?? 1)}
                                  onChange={(e) => updateWhyVideoSort(video.id, e.target.value)}
                                />
                              </td>
                              <td className="text-nowrap">
                                <Button size="sm" variant="outline-primary" onClick={() => onEditWhyVideo(video)}>
                                  Edit
                                </Button>{' '}
                                <Button size="sm" variant="outline-danger" onClick={() => deleteWhyVideo(video.id)}>
                                  Delete
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                        {!whyVideosLoading && sortedWhyVideos.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center text-muted">
                              No videos yet. The landing page will use the fallback cards until at least one active video is added.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>

              <Card className="mt-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <div className="fw-semibold">Fallback cards</div>
                      <div className="text-muted small">These cards preserve the current layout when the video gallery is empty.</div>
                    </div>
                    <div className="text-muted small">{sortedWhyItems.length} item(s)</div>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <Table hover responsive bordered size="sm" className="mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Title</th>
                          <th>Description</th>
                          <th style={{ width: 110 }}>Image</th>
                          <th style={{ width: 90 }}>Active</th>
                          <th style={{ width: 90 }}>Sort</th>
                          <th style={{ width: 180 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedWhyItems.map((item) => (
                          <tr key={item.id}>
                            <td className="fw-semibold">{item.title}</td>
                            <td className="text-muted small">{item.desc}</td>
                            <td>
                              {item.background_image_url ? (
                                <img
                                  src={resolveAssetUrl(item.background_image_url)}
                                  alt={item.title || 'Why Homevilas item'}
                                  style={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 8 }}
                                  loading="lazy"
                                />
                              ) : (
                                <span className="text-muted small">No image</span>
                              )}
                            </td>
                            <td>
                              <Badge bg={item.is_active !== false ? 'success' : 'secondary'}>
                                {item.is_active !== false ? 'Active' : 'Hidden'}
                              </Badge>
                            </td>
                            <td>{Number(item.sort_order ?? 0)}</td>
                            <td className="text-nowrap">
                              <Button size="sm" variant="outline-primary" onClick={() => onEditWhyItem(item)}>
                                Edit
                              </Button>{' '}
                              <Button size="sm" variant="outline-danger" onClick={() => deleteWhyItem(item.id)}>
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {sortedWhyItems.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center text-muted">
                              No fallback cards yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>
      </Tabs>
    </div>
  );
};

export default ContentBlogsPage;
